import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, X, ChevronRight, Inbox, CheckCircle2, Clock4, ListChecks } from "lucide-react";
import { TID } from "@/constants/testIds";
import { API_BASE } from "@/lib/api";
import { ClinivonMark } from "@/components/site/Logo";

const STATUS_OPTIONS = [
  { v: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { v: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-700" },
  { v: "in_progress", label: "In Progress", color: "bg-violet-100 text-violet-700" },
  { v: "closed", label: "Closed", color: "bg-emerald-100 text-emerald-700" },
];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [token] = useState(() => {
    try { return window.localStorage.getItem("clinivon_token"); } catch (_) { return null; }
  });
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE}/admin/inquiries`, { headers: authHeaders }),
        fetch(`${API_BASE}/admin/stats`, { headers: authHeaders }),
      ]);
      if (r1.status === 401 || r2.status === 401) { logout(); return; }
      const d1 = await r1.json(); const d2 = await r2.json();
      setInquiries(d1.items || []);
      setStats(d2);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const logout = () => {
    window.localStorage.removeItem("clinivon_token");
    window.localStorage.removeItem("clinivon_email");
    navigate("/admin/login");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/admin/inquiries/${id}`, {
        method: "PATCH", headers: authHeaders, body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Update failed");
      toast.success("Status updated");
      setInquiries((p) => p.map((x) => x.id === id ? { ...x, status: newStatus } : x));
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await fetch(`${API_BASE}/admin/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const statusBadge = (s) => {
    const o = STATUS_OPTIONS.find((x) => x.v === s) || STATUS_OPTIONS[0];
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${o.color}`}>{o.label}</span>;
  };

  const STAT_CARDS = [
    { label: "Total", value: stats.total, icon: Inbox, color: "text-[#0A2540]", testId: TID.admin.statsTotal },
    { label: "New", value: stats.new, icon: ListChecks, color: "text-blue-600" },
    { label: "In Progress", value: stats.in_progress, icon: Clock4, color: "text-violet-600" },
    { label: "Closed", value: stats.closed, icon: CheckCircle2, color: "text-emerald-600" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <ClinivonMark size={36} />
            <div>
              <div className="font-display font-bold text-[#0A2540] text-sm tracking-tight">CLINIVON</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-500 uppercase">Admin</div>
            </div>
          </Link>
          <button data-testid={TID.admin.logout} onClick={logout} className="text-sm font-medium text-slate-600 hover:text-[#0A2540] flex items-center gap-1.5">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display font-semibold text-3xl text-[#0A2540] mb-2">Inquiries</h1>
        <p className="text-sm text-slate-500 mb-8">Manage submissions and download client documents.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white border border-slate-200 p-5" {...(c.testId ? { "data-testid": c.testId } : {})}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">{c.label}</span>
                <c.icon size={16} className={c.color} />
              </div>
              <div className="font-display font-bold text-3xl text-[#0A2540] tabular">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Company</th>
                  <th className="px-5 py-3">Services</th>
                  <th className="px-5 py-3">Priority</th>
                  <th className="px-5 py-3">Files</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>
                ) : inquiries.length === 0 ? (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">No inquiries yet.</td></tr>
                ) : inquiries.map((q) => (
                  <tr key={q.id} data-testid={TID.admin.inquiryRow(q.id)} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="font-medium text-[#0A2540]">{q.full_name}</div>
                      <div className="text-xs text-slate-500">{q.email}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{q.company_name || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-600 max-w-[200px] truncate">{(q.services || []).slice(0, 2).join(", ") || "—"}{q.services?.length > 2 ? ` +${q.services.length - 2}` : ""}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${q.priority === "Critical" ? "text-red-600" : q.priority === "High" ? "text-amber-600" : "text-slate-500"}`}>{q.priority}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{q.files?.length || 0}</td>
                    <td className="px-5 py-3">{statusBadge(q.status)}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{new Date(q.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        data-testid={TID.admin.inquiryView(q.id)}
                        onClick={() => setSelected(q)}
                        className="text-[#0A2540] hover:text-[#10B981] inline-flex items-center gap-1 text-xs font-semibold"
                      >
                        View <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-end" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:w-[520px] h-full sm:h-screen overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Inquiry</div>
                <h2 className="font-display font-semibold text-xl text-[#0A2540]">{selected.full_name}</h2>
                <p className="text-sm text-slate-500">{selected.email} · {selected.phone_number}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <Row k="Company" v={selected.company_name} />
              <Row k="Job Title" v={selected.job_title} />
              <Row k="Industry" v={selected.industry} />
              <Row k="Employees" v={selected.employees} />
              <Row k="Location" v={selected.location} />
              <Row k="Priority" v={selected.priority} />
              <Row k="Preferred Contact" v={`${selected.preferred_contact_method} · ${selected.preferred_contact_time}`} />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Services</div>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.services || []).map((s) => (
                    <span key={s} className="text-xs px-2 py-1 bg-slate-100 text-slate-700">{s}</span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Description</div>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selected.description}</p>
              </div>
              {selected.files?.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Attachments</div>
                  <ul className="space-y-2">
                    {selected.files.map((f) => (
                      <li key={f.file_id} className="flex items-center justify-between border border-slate-200 px-3 py-2">
                        <span className="text-xs text-slate-700 truncate">{f.filename} · {(f.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => downloadFile(f.file_id, f.filename)} className="text-[#0A2540] hover:text-[#10B981]">
                          <Download size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => updateStatus(selected.id, s.v)}
                      className={`text-xs px-3 py-1.5 border transition-colors ${selected.status === s.v ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0A2540]"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-0.5 shrink-0">{k}</span>
      <span className="text-sm text-slate-700 text-right">{v || "—"}</span>
    </div>
  );
}
